
import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment } from 'react';
import { useLocation, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import ClearIcon from '@mui/icons-material/Clear';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import {
  currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom,
  isSearchBoxShowAtom, isSaveLoadingAtom
} from 'library/store';

import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { IconButton, InputAdornment, MenuItem, Typography, FormHelperText, Link } from '@mui/material';
import NumberFormat from 'react-number-format';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MoreIcon from '@mui/icons-material/More';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useTellerOperationService, { useBasicFilterTellerOperation } from './services/TellerOperation';

import useBillingTypeService from 'features/setup/services/BillingType';

import { ITellerOperation, defaultTellerOperation, ITellerOperationBilling, ITellerOperationMode, defaultTellerOperationMode } from './models/TellerOperation';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import ExtensionValueField from 'components/ui/ExtensionValueField';

import useBillingService from './services/Billing';

import IEnumeration, { IEnumerationItem, Enum_BANK, Enum_BANK_ACCOUNT_NUMBER, Enum_MOBILE_OPERATOR } from 'features/configuration/models/Enumeration';

import useEnumerationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';


import useRegistrationService, { useBasicFilterRegistration } from 'features/production/services/Registration';
import { IRegistration } from 'features/production/models/Registration';
import { isFalsy } from 'utility-types';
import { DatePicker } from '@mui/x-date-pickers';
import { debounce, sum } from 'lodash';
import BasicImportForm from 'components/ui/BasicImportForm';
import { IBillingType } from 'features/setup/models/BillingType';

import useUtils from 'library/utils';
import DeleteIcon from '@mui/icons-material/Delete';

export const TellerOperationForm: FC<ITellerOperation> = (props: ITellerOperation = defaultTellerOperation) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const registrationId = queryParams.get('registrationId');

  const { id } = useParams();
  const { language: lg, allowedOperationModes, roleEntities } = useRecoilValue(currentUserSessionAtom);

  const { parseDate } = useUtils();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer, openEntityPrintDrawer, checkEntitySaveAuthorization } = entityService();

  const { createTellerOperation, updateTellerOperation, createBasicImportTellerOperation } = useTellerOperationService();

  const { getBillingsByRegistration } = useBillingService();

  const { getBillingTypes } = useBillingTypeService();

  const { getRegistration } = useRegistrationService();

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();


  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterTellerOperation = useBasicFilterTellerOperation(
    (event: React.MouseEvent<unknown>, row: ITellerOperation) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const [openRegistrationFilter, setOpenRegistrationFilter] = useState(false);
  const basicFilterRegistration = useBasicFilterRegistration(
    async (event: React.MouseEvent<unknown>, row: IRegistration) => {
      const { id, registrationCode, firstName, lastName, schoolYearClassName, schoolYearName } = row;

      setValue('firstName', firstName);
      setValue('lastName', lastName);
      setValue('schoolYearClassName', schoolYearClassName);
      setValue('schoolYearName', schoolYearName);

      setValue('registrationId', id);
      setValue('registrationCode', registrationCode);

      await loadBillingByRegistration(id);

      setOpenRegistrationFilter(false);
    }
  );


  const loadBillingByRegistration = async (registrationId: number) => {
    const arrBillings = await getBillingsByRegistration(registrationId);

    reset({
      ...getValues(),
      tellerOperationBillings: arrBillings.filter(b => !b.isCancelled).map((bil) => ({ ...bil, billingId: bil.id, amount: 0 }))
    })
  }



  const emptyFunc = (obj: any) => { }

  const methods = useForm<ITellerOperation>({ defaultValues: props }); // here props is default or defaultEx
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchRegistrationId = watch('registrationId');

  //const watchType = watch('type');

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<ITellerOperation>, Error, ITellerOperation>(
    _id > 0 ? updateTellerOperation : createTellerOperation, {
    onSuccess: (data: IResult<ITellerOperation>) => {
      enqueueSnackbar(t('Operation done !!!'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);
      setCurrentFormNameAtom(`${t('Teller operation')} - # ${data.data.id} # - ${data.data.isCancelled ? `(${t('Cancelled')})` : ''}`);
      queryClient.invalidateQueries(['TellerOperation', data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });


  const { isLoading: isLoadingBasicImport, isError: isErrorBasicImport, error: errorBasicImport, mutate: mutateBasicImport } = useMutation<IResult<boolean>, Error, ITellerOperation[]>(
    createBasicImportTellerOperation, {
    onSuccess: (data: IResult<boolean>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      setOpenBasicImportForm(false);
      //_setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);

      //queryClient.invalidateQueries(['Product',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
      setOpenBasicImportForm(false);
    }
  });

  const { data: _data, refetch } = useQuery<ITellerOperation>(['TellerOperation', _id], () => retrieveEntity('TellerOperation', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: enumItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'TellerOperation'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_BANK, Enum_BANK_ACCOUNT_NUMBER, Enum_MOBILE_OPERATOR,]));


  const { data: billingTypes } = useQuery<IBillingType[]>(['BillingTypes', 'SchoolYearClassTemplate'], () => getBillingTypes
    ({ code: '', name: '', description: '' }));

  const cellEditableMode = (row: ITellerOperationMode, cellId: keyof ITellerOperationMode) => {
    return true;
  }

  const cellAllowedModeAmount = (row: ITellerOperationMode, valueAmount: number) => {

    debouncedAmountMode();
    return true;
  }

  const [totalAmountMode, setTotalAmountMode] = useState<number>(0);
  const debouncedAmountMode = useRef(
    debounce(() => {
      const sumAmount = sum(getValues().tellerOperationModes.map(({ amount }) => amount));
      setTotalAmountMode(sumAmount);
    }, 300)
  ).current;


  const getInputAdornmentEmployeeSalaryItem = (row: ITellerOperationMode, cellId: keyof ITellerOperationMode) => ({

    toolTip: t('Expression'),
    icon: DeleteIcon,
    onClickIcon: (event: any, index: number, row: ITellerOperationMode) => {
      (refRemoveTellerOperationMode.current ?? emptyFunc)(index);
      debouncedAmountMode();
    }
  })

  const getOperationModeOptions =
    (row: ITellerOperationMode, cellId: keyof ITellerOperationMode, opts: { value: string | number, name: string, }[]): { value: string | number, name: string }[] => {

      return [{ value: 'E', name: t('Cash') }, { value: 'C', name: t('Check') },
      { value: 'M', name: t('Mobile') }, { value: 'T', name: t('Cart') },
      { value: 'B', name: t('Bank transfert') }, { value: 'O', name: t('Other') },
      { value: 'P', name: t('Payment') }].filter(x => allowedOperationModes.includes(x.value));
    }

    const getInputAdornmentTellerOperationMode = (row: ITellerOperationMode, cellId: keyof ITellerOperationMode) => ({

    toolTip: t('Expression'),
    icon: DeleteIcon,
    onClickIcon: (event: any, index: number, row: ITellerOperationMode) => {
      (refRemoveTellerOperationMode.current ?? emptyFunc)(index);
      debouncedAmountMode();
    }
  })

  const [headTellerOperationModeCells, setHeadTellerOperationModeCells] = useState<HeadCell<ITellerOperationMode>[]>([]);
  useEffect(() => {
    setHeadTellerOperationModeCells([
      {
        id: 'type', label: t('Mode'), display: true, type: 'string', width: 50, isEditable: cellEditableMode,
        getOptions: getOperationModeOptions, getInputAdornment: getInputAdornmentTellerOperationMode
      },
      {
        id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 50, isEditable: cellEditableMode,
        isAllowed: cellAllowedModeAmount
      },

    ])
  }, [t, i18n])

  const refAppendTellerOperationModes = useRef<(value: Partial<FieldArray<ITellerOperation>> | Partial<FieldArray<ITellerOperation>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateTellerOperationMode = useRef<(index: number, value: Partial<FieldArray<ITellerOperation>>) => void>(null);
  const refRemoveTellerOperationMode = useRef<(index: number) => void>(null);

  const handleAddTellerOperationModes = (event: any) => {

    if(getValues().tellerOperationModes.some(x => x.amount ===0 || isFalsy(x.type)))
      return;

    const curAmountMode = sum(getValues().tellerOperationModes.map(({ amount }) => amount));
    const curAmountBilling = sum(getValues().tellerOperationBillings.map(({ amount }) => amount));

    const diff = curAmountBilling - curAmountMode; Math.max()
    const amount = Math.max(0, diff);

    (refAppendTellerOperationModes.current ?? emptyFunc)(
      { ...defaultTellerOperationMode, amount });

    debouncedAmountMode();
  }

  const [openOperationModeDetails, setOpenOperationModeDetails] = useState<boolean>(false);
  const [operationModeIndex, setOperationModeIndex] = useState<number>(-1);

  const tellerOperationModeRowActionIcon = (tellerOperationMode: ITellerOperationMode): ActionIconTableRow<ITellerOperation, ITellerOperationMode> => {
    const res: ActionIconTableRow<ITellerOperation, ITellerOperationMode> = {
      toolTip: 'remove',
      icon: MoreIcon,
      hasAction: (index: number, row: ITellerOperationMode) => true,
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: ITellerOperationMode) => {

        console.log(row);
        if (isFalsy(row.type) || row.type === 'E')     
          return;
        
        setOperationModeIndex(index);
        setOpenOperationModeDetails(true);
        //(refRemoveTellerOperationEx.current??emptyFunc)(index);            
      }
    }
    return res;
  }

  const cellEditableBilling = (row: ITellerOperationBilling, cellId: keyof ITellerOperationBilling) => {
    return true;
  }

  const cellAllowedAmount = (row: ITellerOperationBilling, valueAmount: number) => {
    const { billingId, parentBillingTypeId, unpaidAmount } = row;

    const parentBilling = getValues().tellerOperationBillings.find(x =>
      x.billingTypeId === parentBillingTypeId && x.unpaidAmount - x.amount > 0);

    if (!isFalsy(parentBilling)) {
      enqueueSnackbar(`${t('You have to pay the dependency billing first !!!')} : ${parentBilling.billingTypeName}`,
        { variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000 });
      return false;
    }

    const allowed = valueAmount >= 0 && valueAmount <= unpaidAmount;
    debouncedAmount();

    return allowed;
  }

  const debouncedAmount = useRef(
    debounce(() => {
      const sumAmount = sum(getValues().tellerOperationBillings.map(({ amount }) => amount));
      setValue('amount', sumAmount);
    }, 300)
  ).current;

  const [headTellerOperationBillingCells, setHeadTellerOperationBillingCells] = useState<HeadCell<ITellerOperationBilling>[]>([]);
  useEffect(() => {
    setHeadTellerOperationBillingCells([
      { id: 'billingTypeName', label: t('Billing'), display: true, type: 'string', width: 25 },
      { id: 'billingAmount', label: t('Total amount'), display: true, type: 'numeric', width: 15, },
      { id: 'amountPaid', label: t('Amount paid'), display: true, width: 15, type: 'numeric', },
      { id: 'dueAmount', label: t('Due amount'), display: true, width: 15, type: 'numeric', },
      { id: 'unpaidAmount', label: t('Unpaid amnt'), display: true, width: 15, type: 'numeric', },
      {
        id: 'amount', label: t('Amount'), display: true, width: 15, type: 'numeric',
        isEditable: cellEditableBilling, isAllowed: cellAllowedAmount
      },

    ])
  }, [t, i18n])
  const refAppendTellerOperationBillings = useRef<(value: Partial<FieldArray<ITellerOperation>> | Partial<FieldArray<ITellerOperation>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateTellerOperationBilling = useRef<(index: number, value: Partial<FieldArray<ITellerOperation>>) => void>(null);
  const refRemoveTellerOperationBilling = useRef<(index: number) => void>(null);


  const tellerOperationBillingRowActionIcon = (tellerOperationBilling: ITellerOperationBilling): ActionIconTableRow<ITellerOperation, ITellerOperationBilling> => {
    const res: ActionIconTableRow<ITellerOperation, ITellerOperationBilling> = {
      toolTip: 'remove',
      icon: tellerOperationBilling.amount === 0 && tellerOperationBilling.unpaidAmount > 0 ? DoneOutlineIcon : ClearIcon,
      hasAction: (index: number, row: ITellerOperationBilling) => getValues().id <= 0 && row.unpaidAmount > 0,
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: ITellerOperationBilling) => {

        ///
        //const {billingId, parentBillingTypeId,unpaidAmount} = row;

        //const parentBilling = getValues().tellerOperationBillings.find(x => 
        //   x.billingTypeId === parentBillingTypeId && x.unpaidAmount-x.amount> 0);



        //const allowed = valueAmount>=0 && valueAmount<=unpaidAmount;

        ///
        const tellerOperationBilling = getValues().tellerOperationBillings.at(index);
        if (isFalsy(tellerOperationBilling)) return;

        const { billingId, parentBillingTypeId, unpaidAmount, amount } = tellerOperationBilling;

        const parentBilling = getValues().tellerOperationBillings.find(x =>
          x.billingTypeId === parentBillingTypeId && x.unpaidAmount - x.amount > 0);

        const amount2Pay = amount === 0 ? unpaidAmount : 0;

        if (!isFalsy(parentBilling) && amount2Pay > 0) {
          enqueueSnackbar(`${t('You have to pay the dependency billing first !!!')} : ${parentBilling.billingTypeName}`,
            { variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000 });
          return false;
        }

        (refUpdateTellerOperationBilling.current ?? emptyFunc)(index, {
          ...tellerOperationBilling,
          amount: amount2Pay
        });

        debouncedAmount();
        //(refRemoveTellerOperationItem.current??emptyFunc)(index);            
      }
    }
    return res;
  }

  const handleAddTellerOperationBilling = (event: any) => {

  }
  // useEffect(() => {

  //   const subscription = watch( (value, { name, type }) => {

  //     if(name?.endsWith('amount') ) {

  //       const sp = name.split('.');
  //       const billingIndex = Number(sp[1]);

  //       const tellerOperationBilling = getValues().tellerOperationBillings.at(billingIndex);
  //       if(isFalsy(tellerOperationBilling)) return;

  //       const {amount, unpaidAmount} = tellerOperationBilling;

  //       const amount2Use = Math.max(0, Math.min(amount, unpaidAmount));

  //       (refUpdateTellerOperationBilling.current??emptyFunc)(billingIndex, 
  //         {...tellerOperationBilling!, amount: amount2Use }); 

  //     }
  //   });  

  //   return () => subscription.unsubscribe();
  // }, [watch]);


  const handleClickSearchRegistration = (event: any) => {
    setOpenRegistrationFilter(true);
  }

  const handleRegistrationNavigationClick = (event: any) => {
    if (roleEntities.some(re => re.entityName === 'Registration'))
      navigate(`/registration/${getValues().registrationId}`);
  };


  // const handleTellerOperationItemSelected = (event: React.MouseEvent<unknown>,index: number,row: ITellerOperationItem) => {      
  //   enumerationItemSelected(index);
  // }

  // const enumerationItemSelected = (index: number) => {
  //   setTellerOperationItemIndex(index);

  //   const enumerationExs = getValues().enumerationExs;
  //   const enumerationItem = getValues().enumerationItems[index];

  //   setValue("currentParentTellerOperationItemCode", enumerationItem.parentTellerOperationItemCode);

  //   (refUpdateTellerOperationItem.current ??emptyFunc)(index, {
  //     ...enumerationItem, 
  //     enumerationItemExs: enumerationExs.map( ({id, name, extensionTypeType, extensionTellerOperationItems}, idx) => {

  //       const enumItemEx = enumerationItem.enumerationItemExs.find(x => x.enumerationExId === id);

  //       return {
  //         id: enumItemEx?.id ??0 , 
  //         enumerationItemId: enumItemEx?.enumerationItemId ?? 0, 
  //         enumerationExId: id,
  //         enumerationExName: name, enumerationExType: extensionTypeType,
  //         enumerationExOptions: getAsOptions(extensionTellerOperationItems, ''), 
  //         extensionValueId: enumItemEx?.extensionValueId ?? 0,
  //         value: enumItemEx?.value ?? ''
  //       }
  //     })
  //   } );

  // }



  //       const handleClickSearchEntity = (event: any) => {
  //         setOpenEntityFilter(true);
  //         }

  //         const handleOkBasicTextFilterEntityForm = () => {
  //           setOpenEntityFilter(false);
  //       }

  //     const handleClickSearchTellerOperation = (event: any) => {
  //       setOpenTellerOperationFilter(true);
  //         }

  // const handleOkBasicTextFilterTellerOperationForm = () => {
  //   setOpenTellerOperationFilter(false);
  //       }

  useEffect(() => {
    setCurrentFormNameAtom(t('Teller operation'));
    setCurrentBasicTextFilterProps(basicFilterTellerOperation);
  }, []);


  useEffect(() => {


  }, [watchRegistrationId]);

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
     and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    if (_id > 0)
      retrieveData('TellerOperation', _id, refetch);
  }, [_id]);


  useEffect(() => {
    async function _() {

      if (!registrationId)
        return;

      const regId = Number(registrationId);
      if (isNaN(regId))
        return;

      const registration = await getRegistration(regId);

      if (isFalsy(registration)) return;

      const { id, registrationCode, firstName, lastName, schoolYearClassName, schoolYearName } = registration;

      setValue('firstName', firstName);
      setValue('lastName', lastName);
      setValue('schoolYearClassName', schoolYearClassName);
      setValue('schoolYearName', schoolYearName);

      setValue('registrationId', id);
      setValue('registrationCode', registrationCode);

      await loadBillingByRegistration(id);
    }

    _();
  }, [registrationId]);



  useEffect(() => {

    if (_data && _data.id > 0) {
      setCurrentFormNameAtom(`${t('Teller operation')} - # ${_data.id} # - ${_data.isCancelled ? `(${t('Cancelled')})` : ''}`);
      reset(_data);

    }
    //setTellerOperationItemIndex( ( (_data?.enumerationItems || []).length > 0)?0 : -1 );
  }, [_data, reset]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    setCurrentFormNameAtom(t('Teller operation'));
    reset(props);
    setTotalAmountMode(0);
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!checkEntitySaveAuthorization('TellerOperation', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();
    if (data.id > 0) {
      enqueueSnackbar(t('You can not update teller operation'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (totalAmountMode !== data.amount) {
      enqueueSnackbar(t('The total amount by payment mode is different of total amount paid'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.amount <= 0) {
      enqueueSnackbar(t('There is no billing to pay'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.tellerOperationModes.filter(x => x.type === 'E').length > 1) {
      enqueueSnackbar(t('It is impossible to have cash payment more than once'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.tellerOperationModes.some(x => x.amount > 0 && isFalsy(x.type))) {
      enqueueSnackbar(`${t('Invalid data !!!')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.firstName.trim() === '' && data.lastName.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    const tellerOperationModes = data.tellerOperationModes.filter(x => x.amount > 0);
    mutate({ ...data, tellerOperationModes });
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('TellerOperation', _id);
  }

  const printData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityPrintDrawer('TellerOperation', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    queryClient.invalidateQueries(['TellerOperation', _id]);
    await retrieveData('TellerOperation', _id, refetch);
    reset(_data);
  }

  const [operationsBasicImport, setOperationsBasicImport] = useState<ITellerOperation[]>([]);
  const [openBasicImportForm, setOpenBasicImportForm] = useState<boolean>(false);
  const basicImportData = async (event: MouseEvent<HTMLButtonElement>) => {
    setOpenBasicImportForm(true);
  }

  const handleBasicImportSave = (event: any) => {

    console.log(operationsBasicImport);

    if (operationsBasicImport.length === 0) {
      enqueueSnackbar(t('There is no data to import'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (!checkEntitySaveAuthorization('TellerOperation', _id)) {
      enqueueSnackbar(t('You can not perform this action'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (operationsBasicImport.some(p => isFalsy(p.registrationId) && isFalsy(p.amount))) {
      enqueueSnackbar(t('There are some operatio without amount or registration id'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (operationsBasicImport.some(p => isFalsy(p.operationDateInput))) {
      enqueueSnackbar(t('Date of operation must be specify in format yyyy-MM-dd in colmun operationDateInput'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const badOperationModes = operationsBasicImport.filter(p =>
      (!isFalsy(p.operationModeCode1) && !allowedOperationModes.includes(p.operationModeCode1)) ||
      (!isFalsy(p.operationModeCode2) && !allowedOperationModes.includes(p.operationModeCode2)) ||
      (!isFalsy(p.operationModeCode3) && !allowedOperationModes.includes(p.operationModeCode3)));

    if (badOperationModes.length !== 0) {
      enqueueSnackbar(`${t('There are some operation with bad mode')} : ${badOperationModes[0].operationModeCode1 || ''} ${badOperationModes[0].operationModeCode2 || ''} ${badOperationModes[0].operationModeCode3 || ''}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const types = billingTypes || [];

    const badBillingTypes = operationsBasicImport.filter(p =>
      (!isFalsy(p.operationBillingCode1) && !types.some(bt => bt.code === p.operationBillingCode1)) ||
      (!isFalsy(p.operationBillingCode2) && !types.some(bt => bt.code === p.operationBillingCode2)) ||
      (!isFalsy(p.operationBillingCode3) && !types.some(bt => bt.code === p.operationBillingCode3)) ||
      (!isFalsy(p.operationBillingCode4) && !types.some(bt => bt.code === p.operationBillingCode4)) ||
      (!isFalsy(p.operationBillingCode5) && !types.some(bt => bt.code === p.operationBillingCode5))
    );

    if (badBillingTypes.length !== 0) {
      enqueueSnackbar(`${t('There are some operation with bad billing type')} : ${badOperationModes[0].operationModeCode1 || ''} ${badOperationModes[0].operationModeCode2 || ''} ${badOperationModes[0].operationModeCode3 || ''}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const badAmountOperations = operationsBasicImport.filter(p =>
      p.amount !== (p.operationBillingAmount1 || 0) + (p.operationBillingAmount2 || 0) +
      (p.operationBillingAmount3 || 0) + (p.operationBillingAmount4 || 0) + (p.operationBillingAmount5 || 0) ||
      p.amount !== (p.operationModeAmount1 || 0) + (p.operationModeAmount2 || 0) + (p.operationModeAmount3 || 0) ||
      p.amount === 0);

    if (badAmountOperations.length !== 0) {
      enqueueSnackbar(`${t('There are some operation with bad amount')} : ${badAmountOperations[0].reference} ${badAmountOperations[0].bearer} ${badAmountOperations[0].operationDateInput}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    mutateBasicImport(operationsBasicImport.map(x => ({
      ...x, tellerOperationBillings: [], tellerOperationModes: [],
      operationDate: parseDate(x.operationDateInput) ?? new Date()
    })));
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={3} columnSpacing={0.1}>
          <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='row'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnPrint' onClick={printData} sx={{ display: 'none' }} />

                <Button id='btnBasicImport' onClick={basicImportData} sx={{ display: 'none' }} />

                <TextField sx={{ width: 'calc(50% - 8px)' }} id="fn" label={lg.startsWith('fr') ? t('Last name') : t('First name')}
                  {...register(lg.startsWith('fr') ? 'lastName' : 'firstName')}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={handleClickSearchRegistration}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  helperText={((getValues().registrationId) > 0 && roleEntities.some(re => re.entityName === 'Registration')) ?
                    <FormHelperText>
                      <Link
                        href="#"
                        onClick={handleRegistrationNavigationClick}
                        sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                      >
                        {t('View registration details')}
                      </Link>
                    </FormHelperText> : null
                  } />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="ln" label={lg.startsWith('fr') ? t('First name') : t('Last name')}
                  {...register(lg.startsWith('fr') ? 'firstName' : 'lastName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                {openRegistrationFilter && <FormDialog open={openRegistrationFilter} maxWidth='md'
                  okText={t('OK')} cancelText='' title={t('Registration filter')} onCancel={() => { }}
                  onClose={() => { setOpenRegistrationFilter(false); }} onOk={() => { setOpenRegistrationFilter(false); }}  >
                  <BasicTextFilterForm<IRegistration> {...basicFilterRegistration} />
                </FormDialog>}
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='row'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="schoolYearName" label={t('School year')}
                  {...register('schoolYearName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="schoolYearClassName" label={t('Class')}
                  {...register('schoolYearClassName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(30% - 8px)' }} id="registrationCode" label={t('Matri.')}
                  {...register('registrationCode')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={12} lg={8} component={Paper} >
            <Stack flexDirection='column'>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="reference" label={t('Reference')} {...register('reference')} />

                <Controller control={control}
                  name='operationDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Operation date')}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <Controller control={control}
                  name='issueDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Issue date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(20% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />
                <Controller
                  render={({ field: { onChange, onBlur, name, value, ref } }) => {
                    return (
                      <NumberFormat
                        label={t('Amount')} sx={{ width: 'calc(15% - 8px)' }} disabled={true}
                        allowEmptyFormatting={false}
                        control={control}
                        thousandSeparator={true}
                        decimalScale={2}
                        onValueChange={(v) => onChange(v.floatValue)}
                        defaultValue={value}
                        value={value}
                        customInput={TextFieldRight}
                      />
                    );
                  }}
                  name={`amount`}
                  control={control}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <ArrayFieldTableEx<ITellerOperation, ITellerOperationBilling, 'id'>
                  key={`TellerOperationBilling -`}
                  mainObject={getValues()} fieldKey='id'
                  headCells={headTellerOperationBillingCells} rowsPathName='tellerOperationBillings'
                  title={`${t('Billing')}(s)`} rowActionIcon={tellerOperationBillingRowActionIcon}
                  //onRowSelected={handleRoleEntitySelected}

                  refAppend={refAppendTellerOperationBillings as MutableRefObject<(value: Partial<FieldArray<ITellerOperation>> | Partial<FieldArray<ITellerOperation>>[], options?: FieldArrayMethodProps) => void>}
                  refUpdate={refUpdateTellerOperationBilling as MutableRefObject<(index: number, value: Partial<FieldArray<ITellerOperation>>) => void>}
                  refRemove={refRemoveTellerOperationBilling as MutableRefObject<(index: number) => void>}

                  //stateSelected={[selectedRequestDataItems, setSelectedRequestDataItems]}

                  toolbarActions={
                    getValues().isArticle ?
                      [
                        { toolTip: `${t('Add')}...`, onClickIcon: handleAddTellerOperationBilling, icon: AddCircleIcon, },
                      ] : []
                  }
                />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={4} component={Paper} >
            <Stack flexDirection='column'>
              <Box sx={{ mt: 1, width: '100%' }} key={` ${getValues().tellerOperationModes.map(x => x.type).join('-')} `} >
                <ArrayFieldTableEx<ITellerOperation, ITellerOperationMode, 'id'>
                  key={`TellerOperationItem - ${getValues().tellerOperationModes.length} ${getValues().id} ${getValues().tellerOperationModes.map(x => x.type).join('-')}`}
                  mainObject={getValues()} fieldKey='id'
                  headCells={headTellerOperationModeCells} rowsPathName='tellerOperationModes'
                  title={`${t('Operation mode')} - Total: ${new Intl.NumberFormat('fr-FR').format(totalAmountMode)}`} rowActionIcon={tellerOperationModeRowActionIcon}
                  //onRowSelected={handleTellerOperationItemSelected} toFixed(2)

                  refAppend={refAppendTellerOperationModes as MutableRefObject<(value: Partial<FieldArray<ITellerOperation>> | Partial<FieldArray<ITellerOperation>>[], options?: FieldArrayMethodProps) => void>}
                  refUpdate={refUpdateTellerOperationMode as MutableRefObject<(index: number, value: Partial<FieldArray<ITellerOperation>>) => void>}
                  refRemove={refRemoveTellerOperationMode as MutableRefObject<(index: number) => void>}

                  //stateSelected={[selectedRequestDataItems, setSelectedRequestDataItems]}

                  toolbarActions={[
                    { toolTip: `${t('Print')}...`, onClickIcon: handleAddTellerOperationModes, icon: AddCircleIcon, },

                  ]}
                />
                {openOperationModeDetails && operationModeIndex >= 0 &&
                  <FormDialog open={openOperationModeDetails} maxWidth='sm' height='35vh'
                    okText='' cancelText='' title={t('Details')} onCancel={() => { }}
                    onClose={() => { setOpenOperationModeDetails(false); }} onOk={() => { setOpenOperationModeDetails(false); }}  >
                    <Stack flexDirection='column'>
                      {getValues().tellerOperationModes[operationModeIndex].type === 'C' &&
                        <Box sx={{ mt: 1, width: '100%' }} >
                          <Controller name={`tellerOperationModes.${operationModeIndex}.checkBankCode`} control={control}
                            render={({ field: { onChange, value } }) => (
                              <TextField select onChange={onChange} value={value} sx={{ width: 'calc(35% - 8px)' }} id="justifiedAbsenceType"
                                label={`${t('Bank')}`} inputProps={{ readOnly: false }} focused >
                                {enumItems && enumItems.filter(e =>
                                  e.enumerationCode === Enum_BANK).map(
                                    (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                                }
                              </TextField>
                            )}
                          />
                          <Controller
                            name={`tellerOperationModes.${operationModeIndex}.checkNumber`}
                            control={control}
                            render={({ field }) => <TextField label={`${t('Number')}`}
                              {...field} sx={{ width: `calc(35% - 8px)` }}
                              inputProps={{ readOnly: false, autoComplete: 'new-password', style: { textTransform: 'none' } }}

                            />}
                          />
                          <Controller control={control}
                            name={`tellerOperationModes.${operationModeIndex}.checkDate`}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                              <DatePicker label={t('Date')}
                                onChange={onChange}
                                value={new Date(value)}
                                slotProps={{ textField: { sx: { width: 'calc(30% - 8px)' } } }}
                              //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                              />)}
                          />
                        </Box>}
                      {getValues().tellerOperationModes[operationModeIndex].type === 'M' && <Box sx={{ mt: 1, width: '100%' }} >
                        <Controller name={`tellerOperationModes.${operationModeIndex}.operatorCode`} control={control}
                          render={({ field: { onChange, value } }) => (
                            <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="unjustifiedAbsenceType"
                              label={`${t('Operator')}`} inputProps={{ readOnly: false }} focused >
                              {enumItems && enumItems.filter(e =>
                                e.enumerationCode === Enum_MOBILE_OPERATOR).map(
                                  (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                              }
                            </TextField>
                          )}
                        />
                        <Controller
                          name={`tellerOperationModes.${operationModeIndex}.phoneNumber`}
                          control={control}
                          render={({ field }) => <TextField label={`${t('Reference')}`}
                            {...field} sx={{ width: `calc(50% - 8px)` }}
                            inputProps={{ readOnly: false, autoComplete: 'new-password', style: { textTransform: 'none' } }}

                          />}
                        />
                      </Box>}
                      {getValues().tellerOperationModes[operationModeIndex].type === 'B' && <Box sx={{ mt: 1, width: '100%' }} >
                        <Controller name={`tellerOperationModes.${operationModeIndex}.transferFromToCode`} control={control}
                          render={({ field: { onChange, value } }) => (
                            <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="unjustifiedAbsenceType"
                              label={`${t('Bank account number')}`} inputProps={{ readOnly: false }} focused >
                              {enumItems && enumItems.filter(e =>
                                e.enumerationCode === Enum_BANK_ACCOUNT_NUMBER).map(
                                  (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                              }
                            </TextField>
                          )}
                        />
                        <Controller
                          name={`tellerOperationModes.${operationModeIndex}.reference`}
                          control={control}
                          render={({ field }) => <TextField label={`${t('Reference')}`}
                            {...field} sx={{ width: `calc(50% - 8px)` }}
                            inputProps={{ readOnly: false, autoComplete: 'new-password', style: { textTransform: 'none' } }}

                          />}
                        />
                      </Box>}
                    </Stack>
                  </FormDialog>}
              </Box>

              {openBasicImportForm && <FormDialog open={openBasicImportForm} maxWidth='sm' height='45vh'
                okText={t('Save')} cancelText={t('Cancel')} title={`${t('Import')} ....`} onCancel={() => { setOpenBasicImportForm(false); }}
                onClose={() => { setOpenBasicImportForm(false); }} onOk={handleBasicImportSave}  >
                <BasicImportForm<ITellerOperation>
                  stateItems={[operationsBasicImport, setOperationsBasicImport]}
                  columns={['registrationId', 'firstName', 'lastName', 'operationDateInput']}
                  defaultItem={defaultTellerOperation} />
              </FormDialog>}
            </Stack>
          </Grid>

        </Grid>
      </Box>
    </FormProvider>
  )
}

